/* =========================================================
   Aesthete — Field Journal theme JS
   - Product option swatches (size, color)
   - Quantity stepper
   - Gallery thumb swap
   - Newsletter stub (hooks into Shopify customer form on the theme side)
   ========================================================= */

(function () {
  'use strict';

  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  /* -----------------------------------------------------
     Swatch groups (size, color) — single-select
     Markup:
       <div data-swatch-group data-name="size" data-value="M">
         <button class="fj-swatch" data-value="XS">XS</button>
         ...
       </div>
     ----------------------------------------------------- */
  function initSwatches() {
    $$('[data-swatch-group]').forEach((group) => {
      const name = group.dataset.name;
      const hidden = document.querySelector(`[data-swatch-input="${name}"]`);
      const swatches = $$('.fj-swatch', group);

      swatches.forEach((sw) => {
        on(sw, 'click', (e) => {
          e.preventDefault();
          swatches.forEach((s) => s.classList.remove('is-active'));
          sw.classList.add('is-active');
          const value = sw.dataset.value;
          group.dataset.value = value;
          if (hidden) hidden.value = value;

          const label = document.querySelector(`[data-swatch-label="${name}"]`);
          if (label) label.textContent = value;

          group.dispatchEvent(new CustomEvent('fj:swatch', { detail: { name, value }, bubbles: true }));
        });
      });
    });
  }

  /* -----------------------------------------------------
     Quantity stepper
     Markup:
       <div class="fj-qty" data-qty>
         <button data-qty-dec>−</button>
         <span class="fj-qty__val" data-qty-val>1</span>
         <button data-qty-inc>+</button>
         <input type="hidden" name="quantity" value="1" data-qty-input>
       </div>
     ----------------------------------------------------- */
  function initQty() {
    $$('[data-qty]').forEach((root) => {
      const dec = root.querySelector('[data-qty-dec]');
      const inc = root.querySelector('[data-qty-inc]');
      const val = root.querySelector('[data-qty-val]');
      const input = root.querySelector('[data-qty-input]');

      const set = (n) => {
        const clamped = Math.max(1, Math.floor(n));
        if (val) val.textContent = String(clamped);
        if (input) input.value = String(clamped);
        if (dec) dec.disabled = clamped <= 1;
      };
      const current = () => (input ? parseInt(input.value, 10) : parseInt(val.textContent, 10)) || 1;

      on(dec, 'click', (e) => { e.preventDefault(); set(current() - 1); });
      on(inc, 'click', (e) => { e.preventDefault(); set(current() + 1); });
      set(current());
    });
  }

  /* -----------------------------------------------------
     Gallery — thumbnail swap
     Markup:
       <div data-gallery>
         <div data-gallery-main><img data-gallery-image src="..."></div>
         <div data-gallery-thumbs>
           <button class="fj-gallery__thumb is-active" data-thumb-src="..."><img src="..."></button>
         </div>
       </div>
     ----------------------------------------------------- */
  function initGallery() {
    $$('[data-gallery]').forEach((gal) => {
      const thumbs = $$('.fj-gallery__thumb', gal);
      const track = gal.querySelector('[data-gallery-track]');

      if (track) {
        // Swipeable track: slides mirror the thumbs; swiping and thumb
        // clicks drive the same scroll position.
        const slides = $$('[data-gallery-slide]', track);
        const setActive = (i) => thumbs.forEach((t, j) => {
          t.classList.toggle('is-active', i === j);
          t.setAttribute('aria-selected', i === j ? 'true' : 'false');
        });
        thumbs.forEach((thumb, i) => {
          on(thumb, 'click', (e) => {
            e.preventDefault();
            track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
            setActive(i);
          });
        });
        let raf;
        on(track, 'scroll', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const i = Math.round(track.scrollLeft / track.clientWidth);
            setActive(Math.max(0, Math.min(slides.length - 1, i)));
          });
        });
        return;
      }

      // Legacy markup: single main image, thumbs swap its src
      const mainImg = gal.querySelector('[data-gallery-image]');
      thumbs.forEach((thumb) => {
        on(thumb, 'click', (e) => {
          e.preventDefault();
          thumbs.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          const src = thumb.dataset.thumbSrc || thumb.querySelector('img')?.src;
          if (src && mainImg) mainImg.src = src;
        });
      });
    });
  }

  /* -----------------------------------------------------
     Species-card swipe panes (art / fact card / field photo)
     Native scroll-snap does the swiping; this only keeps the
     dots in sync.
     ----------------------------------------------------- */
  function initCardSwipe() {
    $$('[data-card-swipe]').forEach((root) => {
      const track = root.querySelector('[data-swipe-track]');
      const dots = $$('.fj-swipe__dots i', root);
      if (!track || !dots.length) return;
      let raf;
      on(track, 'scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / track.clientWidth);
          dots.forEach((d, j) => d.classList.toggle('on', i === j));
        });
      });
    });
  }

  /* -----------------------------------------------------
     Add-to-cart (Shopify)
     Markup:
       <form data-cart-form action="/cart/add" method="post">
         <input type="hidden" name="id" value="{{ variant.id }}">
         ...
       </form>
     Falls back to native form submission if fetch fails.
     ----------------------------------------------------- */
  function initCart() {
    $$('[data-cart-form]').forEach((form) => {
      on(form, 'submit', async (e) => {
        // Only intercept if we have fetch + cart drawer hook. Otherwise let it POST normally.
        if (!window.fetch) return;
        e.preventDefault();
        const btn = form.querySelector('[data-cart-submit]');
        const originalText = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
        try {
          const formData = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error('Add failed');
          if (btn) btn.textContent = 'Added ✓';
          window.dispatchEvent(new CustomEvent('fj:cart:added'));
          // Update cart count in header and pulse the icon
          updateCartCount({ bump: true });
          setTimeout(() => { if (btn) { btn.disabled = false; btn.textContent = originalText; } }, 1600);
        } catch (err) {
          // Fall back to native submission
          form.submit();
        }
      });
    });
  }

  async function updateCartCount(opts) {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const count = cart.item_count;
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = String(count);
        // Only the badge has the hidden attribute treatment;
        // the inline drawer link shows "Cart · 0" naturally.
        if (el.classList.contains('fj-header__cart-badge')) {
          if (count > 0) el.removeAttribute('hidden');
          else el.setAttribute('hidden', '');
        }
      });
      const icon = document.querySelector('.fj-header__cart-icon');
      if (icon) {
        icon.setAttribute('aria-label', `Cart, ${count} items`);
        if (opts && opts.bump) {
          icon.classList.remove('is-bumped');
          // Reflow so the animation can re-trigger if pressed twice fast
          // eslint-disable-next-line no-unused-expressions
          icon.offsetWidth;
          icon.classList.add('is-bumped');
          setTimeout(() => icon.classList.remove('is-bumped'), 500);
        }
      }
    } catch (e) { /* ignore */ }
  }

  /* -----------------------------------------------------
     Mobile nav drawer
     Markup:
       <header class="fj-header">
         <nav class="fj-header__nav" id="fj-nav">…</nav>
         <button data-nav-toggle aria-controls="fj-nav" aria-expanded="false">…</button>
         <div data-nav-backdrop hidden></div>
       </header>
     ----------------------------------------------------- */
  function initNav() {
    const header = document.querySelector('.fj-header');
    const toggle = document.querySelector('[data-nav-toggle]');
    const backdrop = document.querySelector('[data-nav-backdrop]');
    if (!header || !toggle) return;

    const close = () => {
      header.classList.remove('is-menu-open');
      document.body.classList.remove('fj-no-scroll');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
      if (backdrop) {
        backdrop.classList.remove('is-visible');
        // Wait for fade-out before removing from layout
        setTimeout(() => { if (!header.classList.contains('is-menu-open')) backdrop.hidden = true; }, 220);
      }
    };
    const open = () => {
      header.classList.add('is-menu-open');
      document.body.classList.add('fj-no-scroll');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      if (backdrop) {
        backdrop.hidden = false;
        // Force reflow so the transition runs
        // eslint-disable-next-line no-unused-expressions
        backdrop.offsetHeight;
        backdrop.classList.add('is-visible');
      }
    };

    on(toggle, 'click', (e) => {
      e.preventDefault();
      if (header.classList.contains('is-menu-open')) close(); else open();
    });

    if (backdrop) on(backdrop, 'click', close);

    // Close when any nav link is tapped
    $$('.fj-header__nav a', header).forEach((a) => on(a, 'click', close));

    // Escape key
    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && header.classList.contains('is-menu-open')) close();
    });

    // If the viewport grows past mobile, close the drawer so desktop nav is clean
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (ev) => { if (ev.matches) close(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  /* -----------------------------------------------------
     Hero pocket-reveal loop
     One continuous camera move inside the hero art panel:
     field photo zooms into the coat, the painting takes over
     with the camera deep on the tee pocket, then a single
     pull-back reveals the pocket and finally the whole shirt.
     ----------------------------------------------------- */
  function initHeroLoop() {
    const panel = document.querySelector('[data-hero-loop]');
    if (!panel) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const cam = panel.querySelector('[data-loop-cam]');
    const art = panel.querySelector('[data-loop-art]');
    const photo = panel.querySelector('[data-loop-photo]');
    const photoImg = photo && photo.querySelector('img');
    const caption = panel.querySelector('[data-loop-caption]');
    if (!cam || !art || !photo || !photoImg) return;

    const pk = (panel.dataset.pocket || '0.5679,0.2686,0.1264,0.1386').split(',').map(Number);
    const POCKET = { x: pk[0], y: pk[1], w: pk[2], h: pk[3] };
    const LABEL = panel.dataset.label || '';
    const FINAL = panel.dataset.final || '';
    const CREDIT = panel.dataset.credit || '';
    const DUR = 14000;

    // pin the painting exactly over the pocket print baked into the tee —
    // same source image, same rect, so the fade between them is invisible
    art.style.left = (POCKET.x * 100) + '%';
    art.style.top = (POCKET.y * 100) + '%';
    art.style.width = (POCKET.w * 100) + '%';
    art.style.height = (POCKET.h * 100) + '%';

    panel.classList.add('is-looping');

    const lerp = (a, b, t) => a + (b - a) * t;
    const clampT = (t) => Math.max(0, Math.min(1, t));
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    let lastCap = '';

    function frame(now) {
      const t = (now % DUR) / DUR;
      const size = panel.clientWidth;
      const pcx = (POCKET.x + POCKET.w / 2) * size;
      const pcy = (POCKET.y + POCKET.h / 2) * size;
      const deep = 0.78 / POCKET.w;

      // While hidden (t > 0.5) the photo resets to its opening scale, so the
      // end-of-loop fade brings it back exactly as the next cycle starts —
      // no zoomed-in ghost frame at the wrap.
      const pz = t < 0.5 ? ease(clampT(t / 0.30)) : 0;
      photoImg.style.transform = 'scale(' + lerp(1.04, 2.35, pz) + ')';
      let photoOn;
      if (t < 0.25) photoOn = 1;
      else if (t < 0.33) photoOn = (0.33 - t) / 0.08;
      else if (t < 0.955) photoOn = 0;
      else photoOn = (t - 0.955) / 0.045;
      photoOn = clampT(photoOn);
      photo.style.opacity = photoOn;
      cam.style.opacity = 1 - photoOn;

      let camScale;
      if (t < 0.50) camScale = deep * lerp(1.045, 1, ease(clampT((t - 0.25) / 0.25)));
      else camScale = lerp(deep, 1, ease(clampT((t - 0.50) / 0.38)));
      cam.style.transformOrigin = pcx + 'px ' + pcy + 'px';
      cam.style.transform = 'scale(' + camScale + ')';
      art.style.opacity = clampT((camScale - 1.9) / 1.2);

      let cap;
      if (t < 0.30) cap = LABEL + ' · Observed from life' + (CREDIT ? ' · ' + CREDIT : '');
      else if (t < 0.55) cap = LABEL + ' · The hand-painted original';
      else if (t < 0.82) cap = LABEL + ' · Cropped to pocket';
      else cap = LABEL + (FINAL ? ' · ' + FINAL : '');
      if (caption && cap !== lastCap) { lastCap = cap; caption.textContent = cap; }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  /* -----------------------------------------------------
     Boot
     ----------------------------------------------------- */
  function boot() {
    initSwatches();
    initQty();
    initGallery();
    initCardSwipe();
    initCart();
    initNav();
    initHeroLoop();
    updateCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
